import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { Button } from './button';
import { uploadContentImage } from '@/lib/upload-content-image';
import { Toggle } from './toggle';
import { Separator } from './separator';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Image as ImageIcon,
  Upload,
  Link as LinkIcon,
  Smile,
  Heading1,
  Heading2,
  Heading3,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo,
  Redo,
} from 'lucide-react';
import { useCallback, useState, useEffect, useRef } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Input } from './input';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
}

export function RichTextEditor({
  content,
  onChange,
  placeholder = 'Begin met typen...',
  className = '',
}: RichTextEditorProps) {
  const [linkUrl, setLinkUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [textFile, setTextFile] = useState<File | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Placeholder.configure({
        placeholder,
      }),
      Image,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-500 underline',
        },
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Listen for custom events to insert images from external components
  useEffect(() => {
    if (!editor) return;

    const handleInsertImage = (event: CustomEvent) => {
      const { imageUrl } = event.detail;
      if (imageUrl && editor) {
        editor.chain().focus().setImage({ src: imageUrl }).run();
      }
    };

    // Add event listener to the editor element
    const editorElement = document.querySelector('.ProseMirror');
    if (editorElement) {
      editorElement.addEventListener(
        'insertImage',
        handleInsertImage as EventListener,
      );
    }

    return () => {
      if (editorElement) {
        editorElement.removeEventListener(
          'insertImage',
          handleInsertImage as EventListener,
        );
      }
    };
  }, [editor]);

  const imageInputRef = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  const addImage = useCallback(() => {
    if (imageUrl && editor) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setImageUrl('');
    }
  }, [editor, imageUrl]);

  // Uploads straight into the bucket and drops the image in at the cursor, so
  // an image in an article body is hosted the same way as one on a record
  // rather than linked from wherever the author happened to have it.
  const uploadAndInsertImage = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (imageInputRef.current) imageInputRef.current.value = '';
      if (!file || !editor) return;
      setUploadingImage(true);
      try {
        const { url } = await uploadContentImage(file);
        editor.chain().focus().setImage({ src: url }).run();
        setImageUrl('');
      } catch (err) {
        console.error('Image upload failed:', err);
        setImageError(
          err instanceof Error ? err.message : 'Uploaden mislukt. Probeer het opnieuw.',
        );
      } finally {
        setUploadingImage(false);
      }
    },
    [editor],
  );

  const setLink = useCallback(() => {
    if (linkUrl && editor) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl })
        .run();
      setLinkUrl('');
    }
  }, [editor, linkUrl]);

  if (!editor) {
    return null;
  }

  // Function to convert plain text with newlines to bullet list HTML
  const convertTextToListIfNeeded = useCallback((text: string) => {
    // Check if the content is already HTML (contains tags)
    if (text.includes('<') && text.includes('>')) {
      return text;
    }

    // Split by double newlines and create a bullet list
    const items = text.split('\n\n').filter((item) => item.trim());
    if (items.length <= 1) {
      return text;
    }

    return `<ul>${items.map((item) => `<li>${item}</li>`).join('')}</ul>`;
  }, []);

  // Sync editor content with prop when it changes externally
  useEffect(() => {
    if (!editor) return;

    const currentHtml = editor.getHTML();
    // Normalize: TipTap uses <p></p> for empty content
    const normalizedCurrent = currentHtml === '<p></p>' ? '' : currentHtml;
    const normalizedContent = content || '';

    // Only update if content has changed from outside (not from our own typing)
    if (normalizedContent !== normalizedCurrent) {
      const formattedContent = convertTextToListIfNeeded(normalizedContent);
      editor.commands.setContent(formattedContent || '');
    }
  }, [content, editor, convertTextToListIfNeeded]);

  return (
    <div className={`border rounded-md ${className}`}>
      <div className="bg-muted p-2 flex flex-wrap items-center gap-1 border-b">
        <Toggle
          size="sm"
          pressed={editor.isActive('bold')}
          onPressedChange={() => editor.chain().focus().toggleBold().run()}
          aria-label="Bold"
        >
          <Bold className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive('italic')}
          onPressedChange={() => editor.chain().focus().toggleItalic().run()}
          aria-label="Italic"
        >
          <Italic className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive('underline')}
          onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
          aria-label="Underline"
        >
          <UnderlineIcon className="h-4 w-4" />
        </Toggle>

        <Separator orientation="vertical" className="mx-1 h-6" />

        <Toggle
          size="sm"
          pressed={editor.isActive('heading', { level: 1 })}
          onPressedChange={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          aria-label="Heading 1"
        >
          <Heading1 className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive('heading', { level: 2 })}
          onPressedChange={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          aria-label="Heading 2"
        >
          <Heading2 className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive('heading', { level: 3 })}
          onPressedChange={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          aria-label="Heading 3"
        >
          <Heading3 className="h-4 w-4" />
        </Toggle>

        <Separator orientation="vertical" className="mx-1 h-6" />

        <Toggle
          size="sm"
          pressed={editor.isActive('bulletList')}
          onPressedChange={() =>
            editor.chain().focus().toggleBulletList().run()
          }
          aria-label="Bullet List"
        >
          <List className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive('orderedList')}
          onPressedChange={() =>
            editor.chain().focus().toggleOrderedList().run()
          }
          aria-label="Ordered List"
        >
          <ListOrdered className="h-4 w-4" />
        </Toggle>

        <Separator orientation="vertical" className="mx-1 h-6" />

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <LinkIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="flex flex-col gap-2">
              <Input
                placeholder="https://example.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
              <div className="flex justify-between">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    editor.chain().focus().unsetLink().run();
                    setLinkUrl('');
                  }}
                  disabled={!editor.isActive('link')}
                >
                  Verwijder Link
                </Button>
                <Button size="sm" onClick={setLink}>
                  Link Toevoegen
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <ImageIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="flex flex-col gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => imageInputRef.current?.click()}
                disabled={uploadingImage}
                className="gap-2"
              >
                <Upload className="h-4 w-4" />
                {uploadingImage ? 'Bezig met uploaden...' : 'Afbeelding uploaden'}
              </Button>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={uploadAndInsertImage}
              />
              <div className="text-xs text-muted-foreground text-center">of</div>
              <Input
                placeholder="https://example.com/image.jpg"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
              />
              <Button size="sm" onClick={addImage} disabled={!imageUrl}>
                Afbeelding Toevoegen
              </Button>
              {imageError && (
                <p className="text-xs text-destructive">{imageError}</p>
              )}
            </div>
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
              >
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="flex flex-col gap-2">
              <Input
                type="file"
                accept=".txt,.md,.html"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setTextFile(file);
                  }
                }}
              />
              <Button
                size="sm"
                onClick={async () => {
                  if (textFile && editor) {
                    try {
                      const text = await textFile.text();
                      editor.commands.setContent(text);
                      setTextFile(null);
                    } catch (error) {
                      console.error('Error reading text file:', error);
                    }
                  }
                }}
                disabled={!textFile}
              >
                Tekst Importeren
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <Smile className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="grid grid-cols-8 gap-2">
              {[
                '😀',
                '😂',
                '😊',
                '🥰',
                '😎',
                '🤔',
                '👍',
                '❤️',
                '🎉',
                '✨',
                '🔥',
                '👏',
                '🙏',
                '💪',
                '🌟',
                '💯',
              ].map((emoji) => (
                <Button
                  key={emoji}
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    editor.chain().focus().insertContent(emoji).run();
                  }}
                >
                  {emoji}
                </Button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <Undo className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <Redo className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div
        className="p-4 min-h-[200px] cursor-text"
        onClick={() => editor.chain().focus().run()}
      >
        <EditorContent
          editor={editor}
          className="prose max-w-none [&_.ProseMirror]:min-h-[168px] [&_.ProseMirror]:outline-none"
        />
      </div>

      {editor && (
        <BubbleMenu
          editor={editor}
          tippyOptions={{ duration: 100 }}
          className="bg-background rounded-md shadow-md border overflow-hidden flex divide-x"
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'bg-muted' : ''}
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'bg-muted' : ''}
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive('underline') ? 'bg-muted' : ''}
          >
            <UnderlineIcon className="h-4 w-4" />
          </Button>
        </BubbleMenu>
      )}
    </div>
  );
}
